import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'store/hooks';
import { useQueryClient } from '@tanstack/react-query';

import {
    BudgetProposalT,
    InscriptionWithMetadata,
    FieldSettingsT,
    TypedOperationT,
    ChapterT,
    NatureT,
} from 'routes/BudgetModule/config/types';

import { BudgetExpenseOrIncome, BudgetPhases } from 'business/constants';
import {
    INSCRIPTION_ID,
    INSCRIPTION_ID_URL_PARAM,
    PROJECT_ID,
} from 'routes/BudgetModule/config/constants';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import NumberFloatingFilter from 'ui/AgGridReact/NumberFloatingFilter';

import PhaseCellRenderer from 'routes/Admin/Budget/BudgetManagement/BudgetList/CellRenderers/PhaseCellRenderer';
import {
    getBudgetProposalFromInscription,
    InscriptionT,
} from 'routes/BudgetModule/shared/entities/inscription';
import { isInscriptionRejectedForVoted } from 'routes/BudgetModule/helpers/statusHelpers';
import {
    TRACKABLE_EVENTS,
    trackEventBudget,
    useGetTrackAgGridGrouping,
} from 'helpers/trackerHelpers';
import OperationCellRenderer from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/Imputations/OrphanInscriptions/components/OperationCellRenderer';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import {
    ColDef,
    ColumnRowGroupChangedEvent,
    GetContextMenuItemsParams,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    RowNode,
} from 'ag-grid-community';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'helpers/reactUseHelper';
import { ExcelExportConfig } from 'routes/BudgetModule/contexts/GridApiContext';
import TextFieldCellRenderer, {
    TextFieldCellRendererParams,
} from './cellRenderers/TextFieldCellRenderer';
import TextHoverCellRenderer from './cellRenderers/TextHoverCellRenderer/TextHoverCellRenderer';
import TextHoverTooltip from './cellRenderers/TextHoverCellRenderer/TextHoverTooltip';
import StatusCellRenderer, {
    StatusCellRendererParams,
} from './cellRenderers/StatusCellRenderer';
import ProjectsCellRenderer from './cellRenderers/ProjectsCellRenderer/ProjectsCellRenderer';
import ActionsCellRenderer from './cellRenderers/ActionsCellRenderer';
import SeeDetailsCellRenderer, {
    SeeDetailsCellRendererParams,
} from './cellRenderers/SeeDetailsCellRenderer';
import ArbitrageInscriptionCellRenderer from './cellRenderers/TextHoverCellRenderer/ArbitrageInscriptionCellRenderer';
import {
    useGetCommentPerInscriptions,
    useUpdateInscription,
} from './hooks/inscriptions';
import StatusHeader from './StatusHeader/StatusHeader';
import OpenButtonCellRenderer, {
    OpenButtonCellRendererParams,
} from './cellRenderers/OpenButtonCellRenderer';
import { areInscriptionsFromSamePhase } from './dataHelpers';
import { getAmountAggData, getInscriptionsColDefs } from './gridHelpers';
import DeleteButtonCellRenderer from './cellRenderers/DeleteButtonCellRenderer';
import ClassifyButtonCellRenderer from './cellRenderers/ClassifyButtonCellRenderer';
import CommentsPerInscriptionContext from './CommentPerInscriptionContext';
import InscriptionsDetails from './InscriptionDetails/InscriptionDetails';
import AgGridReactExposingApi from '../AgGridReactExposingAPI';
import { useManageInscriptionsDisplayModes } from './hooks/useManageInscriptionsDisplayModes';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import {
    onColumnMoved,
    onColumnResized,
    onColumnVisible,
} from '../../BudgetProposal/Resource/ExpenseIncome/Imputations/helpers/gridHelpers';
import { useColumnSettings } from './hooks/inscriptionsSettings';
import { sideBar } from '../../BudgetProposal/Resource/ExpenseIncome/gridHelpers/gridHelpers';
import ChapterCellRenderer from '../../BudgetProposal/Resource/ExpenseIncome/Imputations/OrphanInscriptions/components/ChapterCellRenderer';
import NatureCellRenderer from '../../BudgetProposal/Resource/ExpenseIncome/Imputations/OrphanInscriptions/components/NatureCellRenderer';
import {
    BUDGET_EXCEL_EXPORT_PARAMS,
    BUDGET_EXCEL_STYLES,
} from 'routes/BudgetModule/config/excelExportHelpers';

const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 36;
// since AgGrid 31 the group row is slighty larger, so we need to add 3px to avoid the useles scrollbar
const ROW_GROUP_SIZE = 153;
const PAGINATION_SIZE = 60;
const ADD_INSCRIPTION_SIZE = 70;

const StyInscriptionGrid = styled(Box, {
    name: 'StyInscriptionGrid',
    shouldForwardProp: (prop) => prop !== 'size' && prop !== 'customGridSize',
})<{ size: number; customGridSize?: string }>(
    ({ theme, size, customGridSize }) => ({
        height: customGridSize || `${size}px`,
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.lightGrey,
        '&&& .ag-header-viewport, &&& .ag-center-cols-container, &&& .ag-center-cols-viewport, &&& .ag-row':
            {
                background: theme.palette.lightGrey,
            },
        '&&& .MuiOutlinedInput-root': {
            background: 'white',
        },
        '&&& .ag-layout-auto-height .ag-center-cols-container': {
            minHeight: 'unset',
        },
        '&&& .ag-header-cell-text': {
            color: theme.palette.darkGrey,
            fontSize: '11.5px',
        },
        '&&& .tag': {
            padding: '0.2em 0.8em !important',
        },
        '&&& .ag-selection-checkbox': {
            marginRight: '4px',
        },
    }),
);

type Props = {
    inscriptions: InscriptionWithMetadata[];
    columns: Array<ColDef>;
    budgetProposals: BudgetProposalT[];
    nbDisplayedInscriptions: number;
    expenseOrIncome: BudgetExpenseOrIncome;
    operations?: TypedOperationT[];
    chapters?: ChapterT[];
    natures?: NatureT[];
    allowRowGroupStatusUpdate?: boolean;
    allowFloatingFilters?: boolean;
    pagination?: boolean;
    paginationPageSize?: number;
    customGridSize?: string;
    queryCacheKey: InscriptionQueryKey;
    showRowGroupPanel?: boolean;
    canAddInscriptions?: boolean;
    suppressHorizontalScroll?: boolean;
    suppressNoRowsOverlay?: boolean;
    excelExport?: ExcelExportConfig;
    settings?: FieldSettingsT[];
    onSettingsUpdate?: (arg1: FieldSettingsT[]) => void;
    onGridReady?: (params: GridReadyEvent) => void;
};

const findInscriptionStringId = (
    inscriptions: InscriptionWithMetadata[],
    inscriptionId?: string,
) => {
    if (inscriptionId === undefined) return undefined;
    const inscriptionIdNum = parseInt(inscriptionId, 10);
    if (Number.isNaN(inscriptionIdNum)) return undefined;

    return inscriptions.find(
        (i) => i.inscription[INSCRIPTION_ID] === inscriptionIdNum,
    )?.inscription;
};

export default function InscriptionsGrid({
    inscriptions,
    columns,
    expenseOrIncome,
    budgetProposals,
    operations,
    chapters,
    natures,
    nbDisplayedInscriptions,
    pagination,
    paginationPageSize,
    allowRowGroupStatusUpdate,
    allowFloatingFilters,
    queryCacheKey,
    showRowGroupPanel,
    canAddInscriptions,
    customGridSize,
    suppressHorizontalScroll = false,
    suppressNoRowsOverlay = false,
    excelExport = undefined,
    settings = undefined,
    onSettingsUpdate = () => {},
    onGridReady: onGridReadyCallback = () => {},
}: Props): React.ReactElement {
    const { columnsSettings, updateSettings } = useColumnSettings(
        onSettingsUpdate,
        settings ?? [],
    );

    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const statusesSettings = useContext(StatusSettingsContext);
    const [gridApi, setGridApi] = React.useState<GridApi | null>(null);
    const [selectedInscriptions, setSelectedInscriptions] = React.useState<
        InscriptionWithMetadata[]
    >([]);

    const { roles: userRoles } = useSelector((state) => state.vision.user);

    const { inscriptionsToDisplay, handleGroupBySideEffectOnInscriptions } =
        useManageInscriptionsDisplayModes(inscriptions);

    const [selectedInscriptionsPhase, setSelectedInscriptionsPhase] =
        React.useState<BudgetPhases | null>(null);

    const { search } = useLocation();
    const navigate = useNavigate();

    const inscriptionId =
        new URLSearchParams(search).get(INSCRIPTION_ID_URL_PARAM) || undefined;
    const [selectedDetailInscription, setSelectedDetailInscription] = useState<
        InscriptionT | undefined
    >(undefined);

    useEffect(() => {
        if (!inscriptionId) return;
        setSelectedDetailInscription(
            findInscriptionStringId(inscriptions, inscriptionId),
        );
    }, [inscriptionId, selectedDetailInscription]);

    const inscriptionIdColumn = INSCRIPTION_ID;

    const { data: commentsPerInscription } = useGetCommentPerInscriptions();

    const { updateInscriptionMutation } = useUpdateInscription(
        dispatch,
        queryClient,
        inscriptions,
        queryCacheKey,
    );

    const commentColumn = columns.find((col) => col.field === 'details');
    commentColumn!.valueGetter = (params: {
        data: InscriptionWithMetadata;
    }) => {
        // params.data is undefined when the grid is grouped and the group row is not expanded
        if (!params.data) return 0;
        return (
            commentsPerInscription?.[
                params.data.inscription[inscriptionIdColumn]
            ] ?? 0
        );
    };

    // Disable AgGrid Menu to appear on right click.
    // This is only applied on amount and comments column.
    const getContextMenuItems = useCallback(
        (params: GetContextMenuItemsParams | undefined) => {
            const colId = params?.column?.getColId();
            if (colId && ['amount', 'comment'].includes(colId)) return [];
            return params?.defaultItems || [];
        },
        [],
    );

    const onSelectionChanged = useCallback(() => {
        if (gridApi) {
            const selectedRows = gridApi.getSelectedRows();
            setSelectedInscriptions(selectedRows);
            if (areInscriptionsFromSamePhase(selectedRows, budgetProposals)) {
                setSelectedInscriptionsPhase(
                    getBudgetProposalFromInscription(
                        selectedRows[0].inscription,
                        budgetProposals,
                    ).phase,
                );
            } else {
                setSelectedInscriptionsPhase(null);
            }
        }
    }, [JSON.stringify(budgetProposals), gridApi]);

    // (explo-AMP Shortcut ID : 28345) Maybe this validation (only for batch status updating) could be done in backend instead.
    const isRowSelectable = useCallback(
        (rowNode: RowNode) => {
            if (!rowNode.data || !rowNode.data.inscription) {
                return false;
            }
            const { inscription } = rowNode.data;
            const { phase } = getBudgetProposalFromInscription(
                inscription,
                budgetProposals,
            );
            return (
                (isInscriptionRejectedForVoted(
                    inscription,
                    phase,
                    statusesSettings,
                ) &&
                    inscription.canEdit) ??
                false
            );
        },
        [budgetProposals, statusesSettings, inscriptions],
    );

    const aggFuncs = useMemo(
        () => ({
            sumExcludeRejected: getAmountAggData(
                budgetProposals,
                statusesSettings,
            ),
        }),
        [budgetProposals, statusesSettings],
    );

    const trackGrouping = useGetTrackAgGridGrouping(
        TRACKABLE_EVENTS.budget.general.gridGrouping,
    );

    const onFilterModified = debounce((event) => {
        if (event.column?.colDef.field !== 'comment') return;
        trackEventBudget(TRACKABLE_EVENTS.budget.general.labelSearch);
    }, 3000);

    const cellRendererParams = (
        column: ColDef,
    ): OpenButtonCellRendererParams &
        SeeDetailsCellRendererParams &
        StatusCellRendererParams &
        TextFieldCellRendererParams => ({
        budgetProposals,
        operations,
        chapters,
        natures,
        onInscriptionUpdate: updateInscriptionMutation.mutate,
        inscriptionIdColumn,
        expenseOrIncome,
        queryCacheKey,
        userRoles,
        setSelectedDetailInscription,
        ...(column.cellRendererParams || {}),
    });

    const onGridReady = useCallback((params: GridReadyEvent) => {
        setGridApi(params.api);
        onGridReadyCallback(params);
        handleGroupBySideEffectOnInscriptions();
    }, []);

    const colDefs: ColDef[] = useMemo(
        () =>
            getInscriptionsColDefs({
                columns,
                columnsSettings,
                cellRendererParams,
            }),
        [
            JSON.stringify(columns),
            JSON.stringify(cellRendererParams),
            JSON.stringify(columnsSettings),
        ],
    );

    const colsToExport = useMemo(
        () =>
            colDefs
                .map((c) => c.field ?? '')
                .filter(
                    (c) =>
                        !['details', 'openButton', 'actions', ''].includes(c),
                ),
        [JSON.stringify(columns)],
    );

    const onColumnRowGroupChanged = useCallback(
        (event: ColumnRowGroupChangedEvent) => {
            if (event.columns !== null) {
                handleGroupBySideEffectOnInscriptions(event);
                const newColDefs: ColDef[] = event.api.getColumnDefs() ?? [];
                updateSettings(newColDefs);
                trackGrouping(
                    event.columns.map((c) => {
                        const colDef = c.getColDef();
                        return colDef.headerName || '';
                    }),
                );
            }
        },
        [],
    );

    const groupPanelSize = showRowGroupPanel ? ROW_GROUP_SIZE : 0;
    const paginationSize = pagination ? PAGINATION_SIZE : 0;
    const addInscriptionSize =
        canAddInscriptions === false ? ADD_INSCRIPTION_SIZE : 0;
    const gridSize =
        ROW_HEIGHT * nbDisplayedInscriptions +
        3 * HEADER_HEIGHT +
        groupPanelSize +
        paginationSize +
        addInscriptionSize;

    const [, setLastSelectedInscription] = useLocalStorage<string | null>(
        'lastSelectedInscription',
    );

    // We stabilize the reference to avoid costly re-renders when ticking checkboxes
    const rowStyle = useRef({
        display: 'flex',
    }).current;
    const autoGroupColumnDef = useRef({
        minWidth: 100,
        width: 350,
    }).current;
    const getRowId = useCallback(
        ({ data }: GetRowIdParams) =>
            `${data.inscription[INSCRIPTION_ID]}/${JSON.stringify(
                data.inscription[PROJECT_ID],
            )}`,
        [],
    );
    const defautColDef = useMemo(
        () => ({
            floatingFilter:
                inscriptions.length > 0 && (allowFloatingFilters || false),
        }),
        [inscriptions.length, allowFloatingFilters],
    );
    return (
        <StyInscriptionGrid
            size={gridSize}
            customGridSize={customGridSize}
            className="ag-theme-material"
        >
            {allowRowGroupStatusUpdate && (
                <StatusHeader
                    selectedInscriptions={selectedInscriptions}
                    inscriptions={inscriptionsToDisplay}
                    phase={selectedInscriptionsPhase}
                    queryCacheKey={queryCacheKey}
                    gridApi={gridApi}
                />
            )}
            <CommentsPerInscriptionContext.Provider
                value={commentsPerInscription}
            >
                <AgGridReactExposingApi
                    sideBar={sideBar}
                    defaultColDef={defautColDef}
                    columnDefs={colDefs}
                    domLayout="normal"
                    rowData={inscriptionsToDisplay}
                    suppressAnimationFrame
                    rowHeight={ROW_HEIGHT}
                    headerHeight={HEADER_HEIGHT}
                    pagination={pagination}
                    suppressHorizontalScroll={suppressHorizontalScroll}
                    suppressNoRowsOverlay={suppressNoRowsOverlay}
                    paginationPageSize={paginationPageSize}
                    getContextMenuItems={getContextMenuItems}
                    getRowId={getRowId}
                    components={{
                        textFieldCellRenderer: TextFieldCellRenderer,
                        textHoverCellRenderer: TextHoverCellRenderer,
                        textHoverTooltip: TextHoverTooltip,
                        statusCellRenderer: StatusCellRenderer,
                        projectsCellRenderer: ProjectsCellRenderer,
                        actionsCellRenderer: ActionsCellRenderer,
                        seeDetailsCellRenderer: SeeDetailsCellRenderer,
                        openButtonCellRenderer: OpenButtonCellRenderer,
                        deleteButtonCellRenderer: DeleteButtonCellRenderer,
                        classifyButtonCellRenderer: ClassifyButtonCellRenderer,
                        phaseCellRenderer: PhaseCellRenderer,
                        arbitrageInscriptionCellRenderer:
                            ArbitrageInscriptionCellRenderer,
                        textFloatingFilter: TextFloatingFilter,
                        numberFloatingFilter: NumberFloatingFilter,
                        operationCellRenderer: OperationCellRenderer,
                        chapterCellRenderer: ChapterCellRenderer,
                        natureCellRenderer: NatureCellRenderer,
                    }}
                    rowStyle={rowStyle}
                    onGridReady={onGridReady}
                    rowSelection="multiple"
                    groupSelectsChildren
                    suppressRowClickSelection
                    onSelectionChanged={onSelectionChanged}
                    onFilterModified={onFilterModified}
                    onColumnRowGroupChanged={onColumnRowGroupChanged}
                    isRowSelectable={isRowSelectable}
                    rowGroupPanelShow={showRowGroupPanel ? 'always' : 'never'}
                    autoGroupColumnDef={autoGroupColumnDef}
                    aggFuncs={aggFuncs}
                    excelExport={excelExport}
                    excelStyles={BUDGET_EXCEL_STYLES}
                    defaultExcelExportParams={BUDGET_EXCEL_EXPORT_PARAMS}
                    exportOption={{ columnKeys: colsToExport }}
                    onColumnMoved={onColumnMoved(updateSettings)}
                    onColumnResized={onColumnResized(updateSettings)}
                    onColumnVisible={onColumnVisible(updateSettings)}
                />
                {selectedDetailInscription && (
                    <InscriptionsDetails
                        open={selectedDetailInscription !== undefined}
                        onClose={() => {
                            const searchParams = new URLSearchParams(search);
                            if (searchParams.has(INSCRIPTION_ID_URL_PARAM)) {
                                searchParams.delete(INSCRIPTION_ID_URL_PARAM);
                                navigate(
                                    {
                                        search: searchParams.toString(),
                                    },
                                    { replace: true },
                                );
                            }
                            setLastSelectedInscription(
                                selectedDetailInscription[
                                    INSCRIPTION_ID
                                ].toString(),
                            );
                            setSelectedDetailInscription(undefined);
                        }}
                        inscriptionId={
                            selectedDetailInscription[INSCRIPTION_ID]
                        }
                        budgetProposal={getBudgetProposalFromInscription(
                            selectedDetailInscription,
                            budgetProposals,
                        )}
                        queryCacheKey={queryCacheKey}
                    />
                )}
            </CommentsPerInscriptionContext.Provider>
        </StyInscriptionGrid>
    );
}
